import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { Loader2, ChefHat } from "lucide-react";
import { Button } from "../ui/button";
import CreateRecipeDialog from "./CreateRecipeDialog";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const AUTO = "__auto__", IGNORE = "__ignore__";
const STATUS = {
  auto: { label: "Automatico", cls: "bg-sky-500/15 text-sky-700" },
  manuale: { label: "Manuale", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" },
  nessuna: { label: "Nessuna ricetta", cls: "bg-amber-500/15 text-amber-700" },
  ignorato: { label: "Ignorato", cls: "bg-muted text-muted-foreground" },
};

export default function RecipeMapPanel({ params, refresh }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [creating, setCreating] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sales/recipe-map", { params }); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [params.from_date, params.to_date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load, refresh]);

  const setMap = async (product, value) => {
    try {
      await api.put("/sales/recipe-map", { product, recipe_id: value === AUTO ? null : value === IGNORE ? "" : value });
      toast.success("Abbinamento salvato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Carico…</div>;
  const rows = data.rows.filter((r) => (filter === "tutti" || r.status === filter) && (!q || r.product.toLowerCase().includes(q.toLowerCase())));
  const counts = data.rows.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});

  return (
    <Card className="rounded-xl overflow-hidden" data-testid="recipe-map-panel">
      <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
        <p className="text-sm font-medium">{data.rows.length} prodotti venduti nel periodo</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(STATUS).map(([k, s]) => <Badge key={k} variant="secondary" className={`rounded-full cursor-pointer ${filter === k ? "ring-2 ring-primary" : ""} ${s.cls}`} onClick={() => setFilter(filter === k ? "tutti" : k)} data-testid={`map-filter-${k}`}>{s.label} {counts[k] || 0}</Badge>)}
        </div>
        <Input placeholder="Cerca prodotto…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 ml-auto w-56" data-testid="map-search" />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Prodotto cassa</TableHead><TableHead className="text-right">Q.tà</TableHead><TableHead className="text-right">Venduto</TableHead><TableHead>Stato</TableHead><TableHead>Ricetta Food Cost</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nessun prodotto.</TableCell></TableRow>}
            {rows.map((r) => {
              const s = STATUS[r.status] || STATUS.nessuna;
              const value = r.status === "ignorato" ? IGNORE : r.status === "manuale" ? r.recipe_id : AUTO;
              return (
                <TableRow key={r.product} data-testid={`map-row-${r.product.replace(/\W+/g, "-")}`}>
                  <TableCell className="font-medium">{r.product}{r.pending > 0 && <span className="ml-2 text-[11px] text-amber-600">{r.pending} da scaricare</span>}</TableCell>
                  <TableCell className="text-right">{r.qty}</TableCell>
                  <TableCell className="text-right">{eur(r.amount)}</TableCell>
                  <TableCell><Badge variant="secondary" className={`rounded-full ${s.cls}`}>{s.label}{r.status === "auto" && r.recipe_name ? ` → ${r.recipe_name}` : ""}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                    <Select value={value} onValueChange={(v) => setMap(r.product, v)}>
                      <SelectTrigger className="h-9 w-56" data-testid={`map-select-${r.product.replace(/\W+/g, "-")}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AUTO}>Automatico (per nome)</SelectItem>
                        <SelectItem value={IGNORE}>Ignora (nessuno scarico)</SelectItem>
                        {data.recipes.map((rc) => <SelectItem key={rc.id} value={rc.id}>{rc.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {r.status === "nessuna" && (
                      <Button size="sm" variant="outline" className="rounded-full whitespace-nowrap" onClick={() => setCreating(r)} data-testid={`create-recipe-${r.product.replace(/\W+/g, "-")}`}><ChefHat className="w-4 h-4 mr-1" /> Crea ricetta</Button>
                    )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <CreateRecipeDialog product={creating} onClose={() => setCreating(null)} onCreated={load} />
    </Card>
  );
}
